import {graphql, useStaticQuery} from 'gatsby';
import {buildImageLookups, createMicrositeImageHooks} from 'microsites/core/chatMicrosite/images/micrositeImageHooks';
import {useMemo} from 'react';
import {images} from './images';
import type {ImageKey} from './images';
import type {ImageLookups, MicrositeImageQueryResult} from 'microsites/core/chatMicrosite/images/micrositeImageHooks';

const HERO_IMAGE_ALIASES = {
  heroCoffee: `hero1`,
  heroMobile: `hero2`,
  // Shared hero components still reference the legacy coffee image IDs, so we map them to Chatiwi art.
} as const satisfies Record<string, ImageKey>;

type ImageAliasKey = keyof typeof HERO_IMAGE_ALIASES;

const isImageKey = (imageId: string): imageId is ImageKey => Object.prototype.hasOwnProperty.call(images, imageId);
const isAliasKey = (imageId: string): imageId is ImageAliasKey => Object.prototype.hasOwnProperty.call(HERO_IMAGE_ALIASES, imageId);

const resolveImageKey = (imageId: string): ImageKey => {
  if (isImageKey(imageId)) {
    return imageId;
  }

  if (isAliasKey(imageId)) {
    return HERO_IMAGE_ALIASES[imageId];
  }

  return imageId as ImageKey;
};

const useMicrositeImageLookups = (): ImageLookups<ImageKey> => {
  const data = useStaticQuery<MicrositeImageQueryResult>(graphql`
    query ChatiwiMicrositeImages {
      allFile(filter: {sourceInstanceName: {eq: "chatiwiImages"}, extension: {in: ["png", "jpg", "jpeg", "webp", "svg"]}}) {
        nodes {
          name
          publicURL
          childImageSharp {
            defaultImage: gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, formats: [AUTO, WEBP, AVIF], quality: 80, breakpoints: [256, 384, 512, 640, 768, 960, 1280], outputPixelDensities: [1, 1.5, 2])
            instagramCardImage: gatsbyImageData(layout: FIXED, placeholder: BLURRED, formats: [AUTO, WEBP, AVIF], quality: 74, width: 420, outputPixelDensities: [1, 1.5])
            heroDesktopImage: gatsbyImageData(layout: FULL_WIDTH, placeholder: NONE, formats: [AUTO, WEBP, AVIF], quality: 68, breakpoints: [640, 960, 1280, 1536], outputPixelDensities: [1, 1.5])
            heroMobileImage: gatsbyImageData(layout: CONSTRAINED, placeholder: NONE, formats: [AUTO, WEBP, AVIF], quality: 66, width: 960, outputPixelDensities: [1, 1.5])
            tinyAvatarImage: gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, formats: [AUTO, WEBP, AVIF], width: 96, height: 96, quality: 86, outputPixelDensities: [1, 2])
          }
        }
      }
    }
  `);

  return useMemo(() => {
    return buildImageLookups<ImageKey>(data.allFile.nodes, images);
  }, [data]);
};

const {useMicrositeImage: baseUseMicrositeImage, useFauxInstagramImage: baseUseFauxInstagramImage} = createMicrositeImageHooks<ImageKey>({
  images,
  useLookups: useMicrositeImageLookups,
});

export const useMicrositeImage = (imageId: string) => baseUseMicrositeImage(resolveImageKey(imageId));
export const useFauxInstagramImage = (imageId: string) => baseUseFauxInstagramImage(resolveImageKey(imageId));
